import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import * as Icons from "lucide-react";
import { LAYOUT, AUTH } from "@/constants/testIds";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

export default function Topbar({ onOpenCmdk, onOpenNav }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const initials = (user?.name || user?.email || "U")
    .split(" ").map(s => s[0]).join("").slice(0, 2).toUpperCase();

  return (
    <header
      data-testid={LAYOUT.topbar}
      className="sticky top-0 z-20 h-16 flex items-center gap-3 px-4 sm:px-6 glass-strong border-b border-white/[0.06]"
    >
      <button
        type="button"
        onClick={onOpenNav}
        className="lg:hidden p-2 rounded-lg hover:bg-white/5 text-white/70 hover:text-white"
        aria-label="Open navigation"
      >
        <Icons.Menu className="w-5 h-5" />
      </button>
      <button
        data-testid={LAYOUT.cmdkTrigger}
        onClick={onOpenCmdk}
        className="flex items-center gap-2 h-9 px-3 rounded-lg glass hover:bg-white/[0.06] text-white/60 hover:text-white transition-colors duration-200 min-w-0 max-w-[420px] flex-1"
      >
        <Icons.Search className="w-4 h-4 shrink-0" />
        <span className="text-sm truncate">Search anything…</span>
        <kbd className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-white/50 font-mono hidden sm:inline">
          {isMac ? "⌘K" : "Ctrl K"}
        </kbd>
      </button>

      <div className="flex items-center gap-1 ml-auto">
        <button
          onClick={() => nav("/leads")}
          className="p-2 rounded-lg hover:bg-white/5 text-white/60 hover:text-white transition-colors duration-200"
          aria-label="Leads inbox"
          title="Leads"
        >
          <Icons.Inbox className="w-4 h-4" />
        </button>
        <button
          onClick={() => nav("/ux-audit")}
          className="p-2 rounded-lg hover:bg-white/5 text-white/60 hover:text-white transition-colors duration-200"
          aria-label="Help and UX audit"
          title="UX audit"
        >
          <Icons.HelpCircle className="w-4 h-4" />
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid={LAYOUT.userMenu}
              className="flex items-center gap-2 h-9 pl-1 pr-3 rounded-lg hover:bg-white/5 transition-colors duration-200"
            >
              <Avatar className="w-7 h-7 bg-gradient-to-br from-[#8B5CF6] to-[#3B82F6]">
                <AvatarFallback className="text-xs text-white bg-transparent">{initials}</AvatarFallback>
              </Avatar>
              <div className="hidden sm:flex flex-col items-start leading-none">
                <span className="text-xs font-medium text-white">{user?.name || "User"}</span>
                <span className="text-[10px] text-white/40 capitalize">{user?.role?.replace("_", " ")}</span>
              </div>
              <Icons.ChevronDown className="w-3.5 h-3.5 text-white/40" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 glass-strong border-white/10">
            <DropdownMenuLabel className="text-white/50 text-xs">{user?.email}</DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-white/10" />
            <DropdownMenuItem className="focus:bg-white/5 text-white/80" onClick={() => nav("/settings")}>
              <Icons.User className="w-4 h-4 mr-2" /> Profile & settings
            </DropdownMenuItem>
            <DropdownMenuItem className="focus:bg-white/5 text-white/80" onClick={() => nav("/roles")}>
              <Icons.ShieldCheck className="w-4 h-4 mr-2" /> Roles
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-white/10" />
            <DropdownMenuItem
              data-testid={AUTH.logoutBtn}
              onClick={logout}
              className="focus:bg-white/5 text-red-400 focus:text-red-300"
            >
              <Icons.LogOut className="w-4 h-4 mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
